import React, { useState } from 'react';
import { styled } from '@withjoy/joykit';
import { useEventScheduleByEventIdQuery } from '../../workspace.graphql.generated';
import type { EventConfiguration } from '../../EventWorkspace.types';
import { EventScheduleDay } from './components/EventScheduleDay';
import type { ScheduleState } from './types';
import ScheduleEditor from '@shared/components/ScheduleEditor';
import { PlusIcon } from '@assets/icons';

type EventScheduleProps = {
  eventConfiguration: EventConfiguration;
};

const ScheduleContainer = styled.div`
  padding: 8px;
  text-align: left;
`;

const ScrollWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  padding-top: 24px;
  padding-bottom: 24px;
  padding-left: 16px;
  padding-right: 16px;
`;

const Add = styled.div<{ backgroundColor?: string; shadow?: string }>`
  display: flex;
  position: absolute;
  z-index: 1;
  bottom: 24px;
  right: 24px;
  overflow: hidden;
  height: 48px;
  width: 48px;
  border-radius: 24px;
  background: ${props => (props.backgroundColor ? props.backgroundColor : '#ffffff')};
  box-shadow: 0px 4px 8px 1px rgba(0, 0, 0, 0.08);
  user-select: none;
  transition: all 0.2s ease-in-out;
  transition-delay: 0ms;
  cursor: pointer;
  &:hover,
  &:focus {
    transform: scale(1.02);
    box-shadow: 0px 4px 9px 1px rgba(0, 0, 0, 0.075);
  }
`;

export { SCHEDULE_TIME_FORMAT } from './EventSchedule.constants';

const EventSchedule: React.FC<EventScheduleProps> = ({ eventConfiguration }) => {
  const [view, setView] = useState<ScheduleState>('view');
  const [editItemId, setEditItemId] = useState<string | null>(null);
  const { eventId } = eventConfiguration;
  const { data, loading } = useEventScheduleByEventIdQuery({
    variables: { eventId: eventId },
    batchMode: 'fast',
  });

  if (view === 'add' || (view === 'edit' && editItemId !== null)) {
    return (
      <ScrollWrapper>
        <ScheduleEditor
          mode={view}
          eventId={eventConfiguration.eventId}
          scheduleItemId={editItemId as string}
          onSuccess={async () => {
            setView('view');
          }}
          onCancel={async () => {
            setView('view');
          }}
          refetch={['EventScheduleByEventId']}
        />
      </ScrollWrapper>
    );
  }

  const scheduleData = data?.getGuestGeneralEventScheduleForEvent;
  return (
    <ScrollWrapper>
      <ScheduleContainer>
        {loading && <div>Loading schedule...</div>}
        {!loading &&
          scheduleData &&
          scheduleData?.formattedSchedules.map(day => {
            return (
              <EventScheduleDay
                key={day.dateISO8601TZ}
                day={day}
                onSelect={event => {
                  setView('edit');
                  setEditItemId(event.id);
                }}
              />
            );
          })}
      </ScheduleContainer>
      <Add
        onClick={event => {
          event.stopPropagation();
          setView('add');
        }}
      >
        <PlusIcon
          width={24}
          height={24}
          style={{ margin: 'auto' }}
        />
      </Add>
    </ScrollWrapper>
  );
};

export default EventSchedule;
